import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ProductsSection = ({ products, loading }) => {
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleCheckout = async (productId) => {
    setCheckoutLoading(productId);
    try {
      const originUrl = window.location.origin;
      
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await axios.post(`${API}/checkout`, {
        product_id: productId,
        origin_url: originUrl
      }, {
        signal: controller.signal,
        timeout: 15000
      });
      
      clearTimeout(timeoutId);

      if (response.data.url) {
        // Add small delay before redirect to ensure state updates
        setTimeout(() => {
          window.location.href = response.data.url;
        }, 100);
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        toast.error("Checkout request timed out. Please try again.");
      } else {
        toast.error("Failed to initiate checkout. Please try again.");
      }
      setCheckoutLoading(null);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "podcast":
        return "bg-purple-100 text-purple-700";
      case "educational":
        return "bg-green-100 text-green-700";
      case "certification":
        return "bg-amber-100 text-amber-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <section id="products" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-base text-blue-900 font-semibold tracking-wide uppercase">Our Products</h2>
          <p className="mt-2 text-4xl lg:text-5xl font-bold text-gray-900">
            Premium Learning Materials
          </p>
          <p className="mt-4 text-lg text-gray-600">
            Explore our curated collection of educational content and certifications
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-blue-900 animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(product.category)}`}>
                      {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                    </span>
                  </div>
                </div>
                
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                  <p className="text-gray-600">{product.description}</p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <span className="text-3xl font-bold text-blue-900">${product.price}</span>
                      <span className="text-gray-500 ml-1">USD</span>
                    </div>
                    <Button
                      data-testid={`buy-btn-${product.id}`}
                      onClick={() => handleCheckout(product.id)}
                      disabled={checkoutLoading === product.id}
                      className="bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white px-6 rounded-full"
                    >
                      {checkoutLoading === product.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Buy Now
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;